import React, { useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SectionHeading from '../../ui/SectionHeading';

const AboutSection = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const statsRef = useRef(null);
  
  const sectors = [
    { name: 'Cyberwarfare', description: 'Investing in advanced technologies in support of sovereign clients\' strategic national security missions, such as counter cybercrime / organized crime, counterterrorism and counterintelligence. Our portfolio companies are composed of elite leaders and staff who bring a deep and rich experience around both defensive and offensive cybersecurity.' },
    { name: 'Hydrocarbon', description: 'Strategic investments in the energy sector with a focus on efficiency and sustainability.' },
    { name: 'Fintech and Digital Assets', description: 'Supporting innovative financial technologies and digital asset solutions revolutionizing payments, investments, and banking.' },
    { name: 'AI', description: 'Supporting breakthrough artificial intelligence solutions with transformative potential across industries.' },
    { name: 'Metals & Minerals', description: 'Investing in sustainable mining operations and innovative processing technologies for critical metals and minerals essential to modern technology.' }
  ];
  
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 * i,
        duration: 0.6,
        ease: [0.4, 0.0, 0.2, 1]
      }
    })
  };
  
  return (
    <section id="about" className="section-padding bg-black relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute top-0 left-0 -translate-y-1/2 -translate-x-1/4 w-[800px] h-[800px] rounded-full bg-primary-500/5 blur-[120px] pointer-events-none"></div>
      
      <div className="container-custom relative z-10">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-white">
          <div className="inline-block">
            <p className="text-sm md:text-base tracking-widest uppercase mb-6 text-primary-300">
              About Us
            </p>
          </div>
          <div className="block mt-4">
            A Vision-Driven Family Office
          </div>
        </h2>
        
        <div className="glass-card-strong p-8 mb-8">
          <div className="space-y-4 text-gray-300">
            <p>
              Caishen Capital invests in seed and growth-stage companies with transformative potential across our focus sectors.
            </p>
            <p>
              The founder started in cybersecurity and cyberwarfare and has become one of the global leaders in the space with 25 years of experience.
            </p>
            <p>
              Cyberwarfare is a deeply geopolitical and dynamic field - the unique expertise, connections and capital gained led to diversification and expansion into fintech and digital assets, AI and hydrocarbons in 2022. An in-house hydrocarbon trading desk has been established successfully leveraging new partnerships with refineries and allocation holders to source various products for our esteemed buyers.
            </p>
          </div>
        </div>
        
        <div className="mt-16 md:mt-24">
          <div id="investment-approach" className="scroll-mt-20"></div>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="glass-card-strong p-8"
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6">Investment Approach</h3>
              <div className="space-y-4 text-gray-300">
                <p>
                  Caishen Capital takes a strategic approach to investments, focusing on companies with exceptional teams, 
                  innovative technologies, and the potential to disrupt established industries.
                </p>
                <p>
                  We invest in seed and growth-stage companies across our five primary focus areas, providing not just capital 
                  but also strategic guidance, industry expertise, and valuable connections.
                </p>
                <p>
                  Our family office structure allows us to take a long-term view, making decisions based on value creation rather than 
                  short-term gains, and forming lasting partnerships with founder teams.
                </p>
              </div>
            </motion.div>
            
            <div>
              <div className="grid grid-cols-1 gap-6" ref={ref}>
                {sectors.map((sector, index) => (
                  <motion.div
                    key={sector.name}
                    className="glass-card p-6 md:p-8"
                    custom={index}
                    initial="hidden"
                    animate={controls}
                    variants={cardVariants}
                  >
                    <h4 className="text-xl font-bold mb-3 text-white">{sector.name}</h4>
                    <p className="text-gray-300">{sector.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Investment criteria section */}
          <div id="investment-criteria" className="mt-24 scroll-mt-20">
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold">Investment Criteria</h3>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: 'Teams', description: 'Exceptional entrepreneurs backed by strong teams and advisors with deep industry expertise and proven execution capabilities.' },
                { title: 'Innovation', description: 'Disruptive technologies positioned to transform entire industries with sustainable competitive advantages and clear market differentiation.' },
                { title: 'Growth Potential', description: 'Companies with proven traction, scalable business models, and significant growth trajectory in markets with large TAM.' }
              ].map((criterion, index) => (
                <motion.div
                  key={criterion.title}
                  className="glass-card p-6 md:p-8 text-center"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <h4 className="text-xl font-bold mb-3 text-white">{criterion.title}</h4>
                  <p className="text-gray-300">{criterion.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Investment stages section */}
          <div id="investment-stages" ref={statsRef} className="mt-24 glass-card-strong p-8 md:p-12 scroll-mt-20">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold">Investment Stages & Ticket Size</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                className="p-6 border border-gray-800 rounded-2xl flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-primary-400">S</span>
                </div>
                <h4 className="text-xl font-bold mb-2">Seed Stage</h4>
                <p className="text-gray-300">
                  $25K-$500K investments in early-stage companies with innovative ideas, strong founding teams, and 
                  initial traction in the market.
                </p>
              </motion.div>
              
              <motion.div
                className="p-6 border border-gray-800 rounded-2xl flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 rounded-full bg-secondary-500/20 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-secondary-400">A</span>
                </div>
                <h4 className="text-xl font-bold mb-2">Series A</h4>
                <p className="text-gray-300">
                  $500K-$2M investments in companies with product-market fit, demonstrable revenue growth, and clear 
                  path to scaling operations.
                </p>
              </motion.div>
              
              <motion.div
                className="p-6 border border-gray-800 rounded-2xl flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 rounded-full bg-accent-gold/20 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-accent-gold">B</span>
                </div>
                <h4 className="text-xl font-bold mb-2">Series B</h4>
                <p className="text-gray-300">
                  $2M-$5M investments in established businesses with significant traction, proven unit economics, and 
                  ready to accelerate market expansion.
                </p>
              </motion.div>
              
              <motion.div
                className="p-6 border border-gray-800 rounded-2xl flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 rounded-full bg-primary-900/30 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-white/80">F</span>
                </div>
                <h4 className="text-xl font-bold mb-2">Follow-On</h4>
                <p className="text-gray-300">
                  We support our portfolio companies through their growth journey with strategic follow-on 
                  investments to maximize long-term value creation.
                </p>
              </motion.div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AboutSection;